import { Injectable } from '@angular/core';
import { FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { Vendor } from '../models/Vendor';
import { VendorInformationChangeRequest } from '../models/VendorInformationChangeRequest';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor() {}

  isNullOrEmpty(myString: string): boolean {
    return myString == null || myString.trim() === '';
  }
  objectToQueryString(obj: any) {
    let str = [];
    for (let p in obj) {
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }
    return str.join('&');
  }
  formatCurrency(currency: number) {
    try {
      let converted = Number(currency);
      if (converted || converted == 0) {
        let changedVal = converted;
        if (converted < 0) {
          changedVal *= -1;
        }
        let value = Number(changedVal.toFixed(2)).toLocaleString();
        let checkFinal = value.split('.');
        if (checkFinal.length > 1) {
          if (checkFinal[1].length == 1) {
            value += '0';
          }
        }
        if (converted >= 0) {
          return `$${value}`;
        } else {
          return `($${value})`;
        }
      }
    } catch (e) {}
    return '--';
  }
  formatCurrencyWithoutDollarSign(currency: number) {
    try {
      let converted = Number(currency);
      if (converted || converted == 0) {
        let changedVal = converted;
        if (converted < 0) {
          changedVal *= -1;
        }
        let value = Number(changedVal.toFixed(2)).toLocaleString();
        let checkFinal = value.split('.');
        if (checkFinal.length > 1) {
          if (checkFinal[1].length == 1) {
            value += '0';
          }
        }
        if (converted >= 0) {
          return `${value}`;
        } else {
          return `(${value})`;
        }
      }
    } catch (e) {}
    return '--';
  }
  getFormValidationErrors(
    form: FormGroup,
    formErrors: any = {},
    parentPath: string = '',
  ): { [key: string]: any } {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      if (control instanceof FormGroup) {
        // Recursively check nested form groups
        this.getFormValidationErrors(control, formErrors, currentPath);
      } else if (control instanceof FormArray) {
        // Handle FormArray
        const formArray = control as FormArray;
        formArray.controls.forEach((ctrl, index) => {
          if (ctrl instanceof FormGroup) {
            this.getFormValidationErrors(
              ctrl,
              formErrors,
              `${currentPath}[${index}]`,
            );
          } else {
            const arrayControlErrors = ctrl.errors;
            if (arrayControlErrors !== null) {
              formErrors[`${currentPath}[${index}]`] = arrayControlErrors;
            }
          }
        });
      } else if (control) {
        // Check for errors on this control
        const controlErrors = control.errors;
        if (controlErrors !== null) {
          formErrors[currentPath] = controlErrors;
        }
      }
    });

    return formErrors;
  }
  getErrorMessages(errors: { [key: string]: any }): { [key: string]: string } {
    const messages: { [key: string]: string } = {};

    Object.keys(errors).forEach((controlName) => {
      const errorObj = errors[controlName];
      let message = '';

      // Process the first error found (in order of priority)
      if (errorObj.required) {
        message = 'This field is required';
      } else if (errorObj.email) {
        message = 'Please enter a valid email address';
      } else if (errorObj.minlength) {
        message = `Minimum length is ${errorObj.minlength.requiredLength} characters`;
      } else if (errorObj.maxlength) {
        message = `Maximum length is ${errorObj.maxlength.requiredLength} characters`;
      } else if (errorObj.pattern) {
        message = 'Invalid format';
      } else if (errorObj.min) {
        message = `Minimum value is ${errorObj.min.min}`;
      } else if (errorObj.max) {
        message = `Maximum value is ${errorObj.max.max}`;
      } else if (errorObj.matDatepickerParse) {
        message = 'Please enter a valid date';
      } else if (errorObj.invalidDateRange) {
        message = 'Invalid date range';
      } else if (errorObj.passwordMismatch) {
        message = 'Passwords do not match';
      } else if (errorObj.notUnique) {
        message = 'This value is already in use';
      } else {
        // For any other validation error, get the first error key
        const firstErrorKey = Object.keys(errorObj)[0];
        message = `Invalid value (${firstErrorKey})`;
      }

      messages[controlName] = message;
    });

    return messages;
  }
  getErrorMessage(err: any): string {
    let errorMessage = '';
    try {
      let error = JSON.parse(err);
      if (typeof error === 'object') {
        errorMessage = '<ul>';
        for (const property in error) {
          if (error.hasOwnProperty('message')) {
            errorMessage = error['message'];
            let errorArray = errorMessage.split('-> ');
            if (Array.isArray(errorArray)) {
              errorMessage = '<ul>';
              errorArray.forEach((errorItem) => {
                errorMessage += '<li>' + errorItem + '</li>';
              });
            }
            break;
          } else {
            let errorArray = error[property];
            if (Array.isArray(errorArray)) {
              errorArray.forEach((errorItem) => {
                errorMessage += '<li>' + errorItem + '</li>';
              });
            }
          }
        }
        if (errorMessage.indexOf('<ul>') >= 0) {
          errorMessage += '</ul>';
        }
      }
    } catch {
      errorMessage = err.error?.message || err.error || err.status || err;
    }
    return errorMessage;
  }
  alphaNumericCompareForSorting(a: any, b: any) {
    a = a ?? '';
    b = b ?? '';
    const aHasLetters = /[a-zA-Z]/.test(a);
    const bHasLetters = /[a-zA-Z]/.test(b);

    if (aHasLetters && bHasLetters) {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        if (aNumeric !== bNumeric) {
          return aNumeric - bNumeric;
        }
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    } else if (aHasLetters) {
      return 1;
    } else if (bHasLetters) {
      return -1;
    } else {
      const aNumeric = parseFloat(a);
      const bNumeric = parseFloat(b);
      if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
        return aNumeric - bNumeric;
      }

      return a.localeCompare(b, undefined, { sensitivity: 'case' });
    }
  }
  testId(id: string) {
    if (id == 'create' || id == 'view' || id == 'edit') {
      return null;
    }

    if (id && id.indexOf('?') >= 0) {
      id = id.split('?')[0];
    }

    return id;
  }
  getUsState() {
    return [
      {
        name: 'Alabama',
        abbreviation: 'AL',
      },
      {
        name: 'Alaska',
        abbreviation: 'AK',
      },
      {
        name: 'American Samoa',
        abbreviation: 'AS',
      },
      {
        name: 'Arizona',
        abbreviation: 'AZ',
      },
      {
        name: 'Arkansas',
        abbreviation: 'AR',
      },
      {
        name: 'California',
        abbreviation: 'CA',
      },
      {
        name: 'Colorado',
        abbreviation: 'CO',
      },
      {
        name: 'Connecticut',
        abbreviation: 'CT',
      },
      {
        name: 'Delaware',
        abbreviation: 'DE',
      },
      {
        name: 'District Of Columbia',
        abbreviation: 'DC',
      },
      {
        name: 'Federated States Of Micronesia',
        abbreviation: 'FM',
      },
      {
        name: 'Florida',
        abbreviation: 'FL',
      },
      {
        name: 'Georgia',
        abbreviation: 'GA',
      },
      {
        name: 'Guam',
        abbreviation: 'GU',
      },
      {
        name: 'Hawaii',
        abbreviation: 'HI',
      },
      {
        name: 'Idaho',
        abbreviation: 'ID',
      },
      {
        name: 'Illinois',
        abbreviation: 'IL',
      },
      {
        name: 'Indiana',
        abbreviation: 'IN',
      },
      {
        name: 'Iowa',
        abbreviation: 'IA',
      },
      {
        name: 'Kansas',
        abbreviation: 'KS',
      },
      {
        name: 'Kentucky',
        abbreviation: 'KY',
      },
      {
        name: 'Louisiana',
        abbreviation: 'LA',
      },
      {
        name: 'Maine',
        abbreviation: 'ME',
      },
      {
        name: 'Marshall Islands',
        abbreviation: 'MH',
      },
      {
        name: 'Maryland',
        abbreviation: 'MD',
      },
      {
        name: 'Massachusetts',
        abbreviation: 'MA',
      },
      {
        name: 'Michigan',
        abbreviation: 'MI',
      },
      {
        name: 'Minnesota',
        abbreviation: 'MN',
      },
      {
        name: 'Mississippi',
        abbreviation: 'MS',
      },
      {
        name: 'Missouri',
        abbreviation: 'MO',
      },
      {
        name: 'Montana',
        abbreviation: 'MT',
      },
      {
        name: 'Nebraska',
        abbreviation: 'NE',
      },
      {
        name: 'Nevada',
        abbreviation: 'NV',
      },
      {
        name: 'New Hampshire',
        abbreviation: 'NH',
      },
      {
        name: 'New Jersey',
        abbreviation: 'NJ',
      },
      {
        name: 'New Mexico',
        abbreviation: 'NM',
      },
      {
        name: 'New York',
        abbreviation: 'NY',
      },
      {
        name: 'North Carolina',
        abbreviation: 'NC',
      },
      {
        name: 'North Dakota',
        abbreviation: 'ND',
      },
      {
        name: 'Northern Mariana Islands',
        abbreviation: 'MP',
      },
      {
        name: 'Ohio',
        abbreviation: 'OH',
      },
      {
        name: 'Oklahoma',
        abbreviation: 'OK',
      },
      {
        name: 'Oregon',
        abbreviation: 'OR',
      },
      {
        name: 'Palau',
        abbreviation: 'PW',
      },
      {
        name: 'Pennsylvania',
        abbreviation: 'PA',
      },
      {
        name: 'Puerto Rico',
        abbreviation: 'PR',
      },
      {
        name: 'Rhode Island',
        abbreviation: 'RI',
      },
      {
        name: 'South Carolina',
        abbreviation: 'SC',
      },
      {
        name: 'South Dakota',
        abbreviation: 'SD',
      },
      {
        name: 'Tennessee',
        abbreviation: 'TN',
      },
      {
        name: 'Texas',
        abbreviation: 'TX',
      },
      {
        name: 'Utah',
        abbreviation: 'UT',
      },
      {
        name: 'Vermont',
        abbreviation: 'VT',
      },
      {
        name: 'Virgin Islands',
        abbreviation: 'VI',
      },
      {
        name: 'Virginia',
        abbreviation: 'VA',
      },
      {
        name: 'Washington',
        abbreviation: 'WA',
      },
      {
        name: 'West Virginia',
        abbreviation: 'WV',
      },
      {
        name: 'Wisconsin',
        abbreviation: 'WI',
      },
      {
        name: 'Wyoming',
        abbreviation: 'WY',
      },
    ];
  }
}
export function mapVendorToChangeRequest(
  vendor: Vendor,
): VendorInformationChangeRequest {
  return {
    vendorNumber: vendor.vendorNumber,
    name: vendor.name,
    legalName: vendor.legalName,
    address: vendor.address,
    addressLine2: vendor.addressLine2 || '',
    isInternational: vendor.isInternational || false,
    city: vendor.city,
    state: vendor.state,
    country: vendor.country,
    zipCode: vendor.zipCode,
    description: vendor.description || '',
    w9Status: vendor.w9Status || '',
    futureBidStatus: vendor.futureBidStatus || '',
    w9StatusReason: vendor.w9StatusReason || '',
    referenceId: vendor.referenceId || '',
    vendorType: vendor.vendorType || '',
    tradeBusinessEngaged: vendor.tradeBusinessEngaged || '',
    receivesRentOrRoyaltyIncome: vendor.receivesRentOrRoyaltyIncome || '',
    applicableTaxIdentificationNumber:
      vendor.applicableTaxIdentificationNumber || '',
    socialSecurityNumber: vendor.socialSecurityNumber || '',
    employerIdentificationNumber: vendor.employerIdentificationNumber || '',
    form1099Applicable: vendor.form1099Applicable || '',
    form1099ApplicableOther: vendor.form1099ApplicableOther || '',
    isIncorporated: vendor.isIncorporated || '',
    corporationType: vendor.corporationType || '',
    medicalLegalServicesStatus: vendor.medicalLegalServicesStatus || '',
    payViaACH: vendor.payViaACH || false,
    bankName: vendor.bankName || '',
    bankRoutingNumber: vendor.bankRoutingNumber || '',
    bankAccountNumber: vendor.bankAccountNumber || '',
    bankAccountType: vendor.bankAccountType || '',
    bankAccountClass: vendor.bankAccountClass || 0,
    digitalCheckEmailAddress: vendor.digitalCheckEmailAddress || '',
    client: vendor.client || '',
    clientId: vendor.clientId || '',
    vendorDepartments: vendor.vendorDepartments || [],
    minorityBusinessEnterprise: vendor.minorityBusinessEnterprise || false,
    existingBusinessRelation: vendor.existingBusinessRelation || false,
    label: vendor.label || '',
    paymentType: vendor.paymentType || '',
    deliveryMethod: vendor.deliveryMethod || '',
    providesMerchandiseOrUtilities:
      vendor.providesMerchandiseOrUtilities || false,
    contacts: vendor.contacts || [],
    documents: vendor.documents || [],
    comments: vendor.comments || [],
    vendorId: vendor.vendorId || '',
    currentApprovers: vendor.currentApprovers || [],
    allApprovers: vendor.allApprovers || [],
    declinedReason: vendor.declinedReason || '',
    id: vendor.id || '',
    workflowStep: vendor.workflowStep || '',
    workflowSteps: Array.isArray(vendor.workflowStep)
      ? vendor.workflowStep
      : [],
    createdBy: vendor.createdBy || '',
    completedDate: vendor.completedDate || null,
    requestedBy: vendor.requestedBy || '',
    isCompleted: vendor.isCompleted || false,
    isActive: vendor.isActive || false,
    workflowName: vendor.workflowName || '',
    createdDate: vendor.createdDate || new Date(),
    workflowId: vendor.workflowId,
  };
}
